import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Store, ShoppingCart, Percent, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuyerMarketplacePage = () => {
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    fetchMarketplace();
  }, []);

  const fetchMarketplace = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/buyer/marketplace`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPortfolios(Array.isArray(res.data.portfolios) ? res.data.portfolios : []);
    } catch (error) {
      console.error('Error fetching marketplace:', error);
      toast.error('Failed to load marketplace');
      setPortfolios([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (portfolioId) => {
    setPurchasing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/buyer/purchase`,
        { portfolio_id: portfolioId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success(`Successfully purchased ${res.data.portfolio_name}!`);
        setSelectedPortfolio(null);
        navigate('/buyer/dashboard');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  const filteredPortfolios = portfolios.filter(p => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return p.name?.toLowerCase().includes(query);
  });

  const getStatusBadge = (status) => {
    const colors = {
      'ACTIVE': { bg: 'rgba(71, 255, 176, 0.1)', color: '#47FFB0' },
      'LAUNCHED': { bg: 'rgba(75, 157, 255, 0.1)', color: '#4B9DFF' },
      'LIVE': { bg: 'rgba(255, 193, 7, 0.1)', color: '#FFC107' }
    };
    const style = colors[status] || colors.ACTIVE;
    return (
      <span style={{
        fontSize: '11px',
        padding: '4px 8px',
        borderRadius: '4px',
        fontWeight: 600,
        background: style.bg,
        color: style.color,
        textTransform: 'uppercase'
      }}>
        {status}
      </span>
    );
  };

  return (
    <div style={{ padding: '40px' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#0B0B0B', marginBottom: '8px' }}>
        NFT Marketplace
      </h1>
      <p style={{ fontSize: '16px', color: '#999', marginBottom: '32px' }}>
        Browse and purchase NFTs from verified creators
      </p>

      {/* Search */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        background: '#FFFFFF',
        padding: '12px 16px',
        borderRadius: '12px',
        border: '1px solid #E6E8EF',
        marginBottom: '32px',
        maxWidth: '500px'
      }}>
        <Search size={20} color="#666" />
        <input
          type="text"
          placeholder="Search collections..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-testid="marketplace-search"
          style={{
            border: 'none',
            background: 'transparent',
            outline: 'none',
            fontSize: '15px',
            color: '#0B0B0B',
            width: '100%'
          }}
        />
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
          Loading marketplace...
        </div>
      ) : filteredPortfolios.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
          {filteredPortfolios.map(p => (
            <div
              key={p.id}
              data-testid={`marketplace-item-${p.id}`}
              style={{
                background: '#FFFFFF',
                borderRadius: '16px',
                overflow: 'hidden',
                border: '1px solid #E6E8EF',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(75, 157, 255, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              onClick={() => setSelectedPortfolio(p)}
            >
              {/* Image */}
              <div style={{
                width: '100%',
                height: '180px',
                background: p.image_url 
                  ? `url(${p.image_url}) center/cover`
                  : 'linear-gradient(135deg, #7C3AED, #4B9DFF)',
              }} />
              
              {/* Info */}
              <div style={{ padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#0B0B0B', margin: 0 }}>
                    {p.name}
                  </h3>
                  {getStatusBadge(p.status)}
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Price</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#47FFB0' }}>
                      ${p.unit_price || '0'}
                    </div>
                  </div>
                  {p.kickback_percent > 0 && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      background: 'rgba(255, 193, 7, 0.1)',
                      padding: '6px 10px',
                      borderRadius: '8px'
                    }}>
                      <Percent size={14} color="#FFC107" />
                      <span style={{ fontSize: '13px', color: '#FFC107', fontWeight: 600 }}>
                        {p.kickback_percent}% kickback
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '60px',
          textAlign: 'center',
          border: '1px solid #E6E8EF'
        }}>
          <Store size={48} color="#4B9DFF" style={{ marginBottom: '16px' }} />
          <h3 style={{ color: '#0B0B0B', fontSize: '20px', marginBottom: '8px' }}>
            {searchQuery ? 'No results found' : 'No Collections Available'}
          </h3>
          <p style={{ color: '#666', fontSize: '14px' }}>
            {searchQuery ? 'Try a different search term' : 'Check back later for new drops'}
          </p>
        </div>
      )}

      {/* Purchase Modal */}
      {selectedPortfolio && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setSelectedPortfolio(null)}
        >
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '32px',
              maxWidth: '500px',
              width: '90%',
              border: '1px solid #E6E8EF'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#0B0B0B', margin: 0 }}>
                Purchase NFT
              </h2>
              <button
                onClick={() => setSelectedPortfolio(null)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <X size={24} color="#666" />
              </button>
            </div>

            {/* Portfolio Preview */}
            <div style={{
              width: '100%',
              height: '200px',
              background: selectedPortfolio.image_url
                ? `url(${selectedPortfolio.image_url}) center/cover`
                : 'linear-gradient(135deg, #7C3AED, #4B9DFF)',
              borderRadius: '12px',
              marginBottom: '20px'
            }} />

            <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#0B0B0B', marginBottom: '8px' }}>
              {selectedPortfolio.name}
            </h3>
            
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              {getStatusBadge(selectedPortfolio.status)}
              {selectedPortfolio.kickback_percent > 0 && (
                <span style={{
                  fontSize: '11px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  background: 'rgba(255, 193, 7, 0.1)',
                  color: '#FFC107',
                  fontWeight: 600
                }}>
                  {selectedPortfolio.kickback_percent}% Kickback
                </span>
              )}
            </div>

            <div style={{
              background: '#FFFFFF',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#999' }}>Price</span>
                <span style={{ color: '#0B0B0B', fontWeight: 600 }}>${selectedPortfolio.unit_price || '0'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#999' }}>Supply</span>
                <span style={{ color: '#0B0B0B' }}>
                  {selectedPortfolio.total_units || selectedPortfolio.quantity || '∞'} units
                </span>
              </div>
            </div>

            <button
              onClick={() => handlePurchase(selectedPortfolio.id)}
              disabled={purchasing}
              data-testid="confirm-purchase-btn"
              style={{
                width: '100%',
                padding: '16px',
                background: purchasing ? '#333' : 'linear-gradient(135deg, #47FFB0, #4B9DFF)',
                color: purchasing ? '#666' : '#000',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 700,
                cursor: purchasing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <ShoppingCart size={20} />
              {purchasing ? 'Processing...' : 'Complete Purchase'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyerMarketplacePage;
